import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { PlannerExperience, PlannerSection } from '../../types/planner';
import { SectionHeader } from '../layout/SectionHeader';
import { ExperienceCard } from './ExperienceCard';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

interface ExperienceSectionProps {
  section: PlannerSection;
  onToggleSelect?: (id: string) => void;
  onOpenDetails?: (experience: PlannerExperience) => void;
  onFocusSection?: () => void;
  onAddActivities?: (section: PlannerSection) => void;
  isAddingActivities?: boolean;
  isAnyOperationInProgress?: boolean;
  onDeleteActivity?: (id: string) => void;
  deletingActivities?: Record<string, boolean>;
}

export const ExperienceSection: React.FC<ExperienceSectionProps> = ({
  section,
  onToggleSelect,
  onOpenDetails,
  onFocusSection,
  onAddActivities,
  isAddingActivities,
  isAnyOperationInProgress = false,
  onDeleteActivity,
  deletingActivities = {},
}) => {
  const theme = useTheme();
  const isRestaurantSection = section.id === 'restaurants' || section.id?.startsWith('restaurants-');
  const showAddAction = (typeof section.dayNumber === 'number' || isRestaurantSection) && onAddActivities;
  const addDisabled = Boolean(isAddingActivities) || isAnyOperationInProgress;
  const showInlineAddMd = Boolean(showAddAction) && section.experiences.length % 2 !== 0;
  const showInlineAddXl = Boolean(showAddAction) && section.experiences.length % 3 !== 0;
  const addLabel = isRestaurantSection ? 'Add more restaurants' : 'Add more activities';
  const titleProps = typeof section.dayNumber === 'number'
    ? {
        variant: 'h4' as const,
        sx: {
          fontWeight: 700,
          color: 'text.primary',
        },
      }
    : undefined;
  const handleAddActivities = () => {
    if (!addDisabled) {
      onAddActivities?.(section);
    }
  };

  const renderAddAction = (mode: 'tile' | 'row') => (
    <Box
      component="button"
      type="button"
      disabled={addDisabled}
      onClick={handleAddActivities}
      sx={{
        appearance: 'none',
        width: '100%',
        font: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: mode === 'tile' ? 1.25 : 1.5,
        minHeight: mode === 'tile' ? { md: 270, lg: 292, xl: 318 } : undefined,
        py: mode === 'tile' ? 3 : 1.5,
        px: mode === 'tile' ? 2.5 : 2,
        border: '2px dashed',
        borderColor: addDisabled ? 'action.disabled' : 'primary.light',
        borderRadius: mode === 'tile' ? 2 : 3,
        cursor: addDisabled ? 'not-allowed' : 'pointer',
        opacity: addDisabled ? 0.5 : 1,
        color: 'text.secondary',
        backgroundColor: mode === 'tile'
          ? alpha(theme.palette.primary.main, 0.025)
          : 'transparent',
        boxShadow: mode === 'tile'
          ? `0 16px 34px ${alpha(theme.palette.common.black, 0.06)}`
          : 'none',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease',
        flexDirection: mode === 'tile' ? 'column' : 'row',
        '&:hover': addDisabled
          ? {}
          : {
              borderColor: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, mode === 'tile' ? 0.055 : 0.04),
              transform: mode === 'tile' ? 'translateY(-3px)' : 'none',
              boxShadow: mode === 'tile'
                ? `0 22px 42px ${alpha(theme.palette.primary.main, 0.12)}`
                : 'none',
            },
        '&:focus-visible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.24)}`,
          outlineOffset: 3,
        },
      }}
    >
      {isAddingActivities ? (
        <CircularProgress size={mode === 'tile' ? 28 : 20} sx={{ color: 'primary.main' }} />
      ) : (
        <AddCircleOutlineIcon
          sx={{
            fontSize: mode === 'tile' ? 42 : 24,
            color: addDisabled ? 'action.disabled' : 'primary.light',
          }}
        />
      )}
      <Box
        component="span"
        sx={{
          color: addDisabled ? 'text.disabled' : 'text.secondary',
          fontSize: mode === 'tile' ? '0.95rem' : '0.875rem',
          fontWeight: 650,
          textAlign: 'center',
          lineHeight: 1.25,
        }}
      >
        {isAddingActivities ? 'Adding...' : addLabel}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        eyebrow="Recommended"
        title={section.title}
        subtitle={section.subtitle}
        titleProps={titleProps}
      />
      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {section.experiences.map((experience) => (
          <ExperienceCard
            key={experience.id}
            experience={experience}
            onToggleSelect={onToggleSelect}
            onOpenDetails={onOpenDetails}
            onFocus={onFocusSection}
            isRestaurant={isRestaurantSection}
            onDeleteActivity={onDeleteActivity}
            deletingActivities={deletingActivities}
            isAnyOperationInProgress={isAnyOperationInProgress}
          />
        ))}
        {showAddAction && (
          <Box
            sx={{
              display: {
                xs: 'none',
                md: showInlineAddMd ? 'flex' : 'none',
                xl: showInlineAddXl ? 'flex' : 'none',
              },
              minWidth: 0,
            }}
          >
            {renderAddAction('tile')}
          </Box>
        )}
      </Box>
      {showAddAction && (
        <Box
          sx={{
            display: {
              xs: 'flex',
              md: showInlineAddMd ? 'none' : 'flex',
              xl: showInlineAddXl ? 'none' : 'flex',
            },
          }}
        >
          {renderAddAction('row')}
        </Box>
      )}
    </Box>
  );
};
